import {Component, OnInit, DestroyRef, inject} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { FormlyFormOptions } from '@ngx-formly/core';
import { map, filter, tap, withLatestFrom } from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import * as R from 'ramda';
import { EditLocationDialogComponent } from '@emm/shared/dialogs/edit-location-dialog/edit-location-dialog.component';
import * as dayjs from 'dayjs';
import { MatDialog } from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Revotes} from '@emm/core/models/revotes.model';
import {MasterMenuFacadeService} from '@emm/management/services/master-menu-facade.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-mms-revote-form',
    templateUrl: './mms-revote-form.component.html',
    styleUrls: ['./mms-revote-form.component.scss'],
})
export class MmsRevoteFormComponent implements OnInit {
    revoteDetail$ = this.masterMenuFacade.revoteDetail$;
    allLocations = this.masterMenuFacade.allDTOMallFSULocations$;
    categories$ = this.masterMenuFacade.categories$;
    selectedCategories = [];
    selectedLocations = []
    selectedRevote: Revotes;
    prevOpenDate: any;
    prevCloseDate: any;
    prevEffectiveDate: any;
    readOnlyState = false;
    isReadOnly$ = this.authFacade.isReadOnly$;

    pipe = new DatePipe('en-US');
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    form: FormGroup;
    options: FormlyFormOptions = {};
    model = {};

    model$ = this.revoteDetail$.pipe(
        filter(RA.isNotNil),
        map((revote: any) => ({
            name: revote.name,
            effectiveDate: revote.effectiveDate,
            openDate: revote.openDate,
            closeDate: revote.closeDate,
            submenus: revote.submenus,
            locations: revote.locations,
            consumer: revote.destination
        })),
        tap((model) => (this.model = model))
    );

    constructor(
        private masterMenuFacade: MasterMenuFacadeService,
        private authFacade: AuthFacadeService,
        private notificationService: NotificationService,
        private fb: FormBuilder,
        public dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.revoteDetail$.subscribe(revoteDetails => {
            if (R.isNotEmpty(revoteDetails) && RA.isNotNull(revoteDetails)) {
                this.initForm(revoteDetails);
                this.selectedRevote = revoteDetails;
                this.masterMenuFacade.updateCategories(this.selectedRevote.destination);
                this.masterMenuFacade.updateLocations(this.selectedRevote.destination);
            }
        })
    }

    selectEffectiveDate(date: string) {
        this.prevEffectiveDate = date;
        this.form.markAsDirty();
    }

    selectOpenDate(date: string) {
        this.prevOpenDate = date;
        this.form.markAsDirty();
    }

    selectCloseDate(date: string) {
        this.prevCloseDate = date;
        this.form.markAsDirty();
    }

    updateAllSelected(checked, checkedValue) {
        this.form.markAsDirty();
        if (checked) {
            this.selectedCategories.push(checkedValue);
        } else {
            this.selectedCategories = this.selectedCategories.filter(s => s !== checkedValue)
        }
    }

    cancel() {
        this.initForm(this.selectedRevote);
    }

    submit(revote: Revotes, reloadAfterUpdate: boolean = true) {
        const updatedRevote: Revotes = {
            name: this.form.get('name').value,
            destination: this.selectedRevote.destination,
            effectiveDate: dayjs(this.prevEffectiveDate).format('YYYY-MM-DD'),
            openDate: dayjs(this.prevOpenDate).format('YYYY-MM-DD'),
            closeDate: dayjs(this.prevCloseDate).format('YYYY-MM-DD'),
            submenus: this.selectedCategories,
            locations: this.selectedLocations,
            id: revote.id
        }
        try {
            this.masterMenuFacade.updateRevote(revote.id, updatedRevote, reloadAfterUpdate)
                .pipe(withLatestFrom(this.revoteDetail$), takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([success]) => {
                        this.notificationService.success(`Saved : ${updatedRevote['name']}`);
                        this.form.markAsPristine();
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } catch (e) {
            this.notificationService.error(`Could not save : ${updatedRevote['name']}`);
        }
    }

    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }

    initForm(revoteDetails) {
        this.form = this.fb.group({
            name: [revoteDetails.name, {nonNullable: true}],
            consumer: [revoteDetails.destination, {nonNullable: true}]
        });
        this.prevOpenDate = revoteDetails.openDate;
        this.prevCloseDate = revoteDetails.closeDate;
        this.prevEffectiveDate = revoteDetails.effectiveDate;
        this.selectedCategories = [];
        revoteDetails.submenus.map((menu) => {
            this.selectedCategories.push(menu);
        });
        this.selectedLocations = revoteDetails.locations.map(
            (location) =>
                ({
                    countryCode: location.countryCode,
                    description: location.description,
                    name: location.name,
                    tag: location.tag,
                    id: location.tag
                })
        );
        const today = dayjs(new Date()).format('YYYY-MM-DD');
        this.readOnlyState = this.prevOpenDate < today;
    }

    editLocations() {
        const dialogRef = this.dialog.open(EditLocationDialogComponent, {
            width: '600px',
            data: {
                selectedLocations: this.selectedLocations,
            }
        });

        return dialogRef.afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result) => {
            if (result !== undefined) {
                this.selectedLocations = result;
                this.form.markAsDirty();
            }
        });
    }
}
